import { useState } from "react";
import { Loader2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { type ClassroomReportRow } from "./types";
import { GenerateDialog } from "./GenerateDialog";

export function ReportDateSearch({
  fixedClassroomId,
  onResult,
}: {
  fixedClassroomId?: number;
  onResult: (r: ClassroomReportRow) => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [classroomIdInput, setClassroomIdInput] = useState("");
  const [date, setDate] = useState(today);
  const [searching, setSearching] = useState(false);
  const [notFoundDate, setNotFoundDate] = useState<string | null>(null);
  const [notFoundReason, setNotFoundReason] = useState<
    "not_found" | "no_data" | "failed" | null
  >(null);
  const [showGenerate, setShowGenerate] = useState(false);

  const resolvedClassroomId =
    fixedClassroomId ?? (classroomIdInput ? Number(classroomIdInput) : null);

  const handleSearch = async () => {
    if (!resolvedClassroomId) return;
    setSearching(true);
    setNotFoundDate(null);
    setNotFoundReason(null);
    try {
      const params = new URLSearchParams({
        classroomId: String(resolvedClassroomId),
        date,
      });
      const r = await fetch(
        `/api/internal/admin/classroom-reports/lookup?${params}`,
      );
      if (r.status === 404) {
        setNotFoundDate(date);
        setNotFoundReason("not_found");
        return;
      }
      const data: ClassroomReportRow = await r.json();
      if (data.status === "no_data") {
        setNotFoundDate(date);
        setNotFoundReason("no_data");
        return;
      }
      if (data.status === "failed") {
        setNotFoundDate(date);
        setNotFoundReason("failed");
        return;
      }
      onResult(data);
    } catch {
      setNotFoundDate(date);
      setNotFoundReason("not_found");
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-2">
        {!fixedClassroomId && (
          <div className="flex flex-col gap-1 w-28">
            <label className="text-xs text-muted-foreground">
              Classroom ID
            </label>
            <Input
              placeholder="e.g. 284"
              value={classroomIdInput}
              onChange={(e) => {
                if (/^\d*$/.test(e.target.value)) {
                  setClassroomIdInput(e.target.value);
                  setNotFoundDate(null);
                  setNotFoundReason(null);
                }
              }}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
          </div>
        )}
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">Date</label>
          <Input
            type="date"
            value={date}
            onChange={(e) => {
              setDate(e.target.value);
              setNotFoundDate(null);
              setNotFoundReason(null);
            }}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            className="w-40"
          />
        </div>
        <Button
          onClick={handleSearch}
          disabled={searching || !resolvedClassroomId}
          className="gap-2"
        >
          {searching ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Search className="size-4" />
          )}
          Search
        </Button>
      </div>
      {notFoundDate && (
        <div className="flex items-center gap-3">
          <p className="text-xs text-muted-foreground">
            {notFoundReason === "no_data"
              ? `No sessions found for ${notFoundDate} when last checked. Want to try again?`
              : notFoundReason === "failed"
                ? `Report generation failed for ${notFoundDate}. Want to try again?`
                : `No report found for ${notFoundDate}.`}
          </p>
          <Button
            size="sm"
            variant="outline"
            className="text-xs h-7"
            onClick={() => setShowGenerate(true)}
          >
            {notFoundReason === "no_data" || notFoundReason === "failed"
              ? "Regenerate"
              : "Generate"}
          </Button>
        </div>
      )}
      {showGenerate && notFoundDate && resolvedClassroomId && (
        <GenerateDialog
          classroomId={resolvedClassroomId}
          date={notFoundDate}
          onClose={() => setShowGenerate(false)}
          onDone={(r) => {
            setShowGenerate(false);
            onResult(r);
          }}
        />
      )}
    </div>
  );
}
